"use client";

import * as React from "react";
import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { DataService } from "@/services/data";
import { getViewportBottomInset } from "@/lib/utils";

interface FancyMultiSelectProps {
    options?: { value: string; label: string }[];
    optionsUrl?: string;
    value?: string; // Comma-separated values
    onValueChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}

const DROPDOWN_MAX_HEIGHT = 192; // max-h-48
const DROPDOWN_GAP = 8;

export function FancyMultiSelect({ options, optionsUrl, value = "", onValueChange, placeholder = "Select options...", disabled = false }: FancyMultiSelectProps) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const controlRef = React.useRef<HTMLDivElement>(null);
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [innerOptions, setInnerOptions] = React.useState<{ value: string; label: string }[]>(options || []);
    const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>({ visibility: "hidden" });

    // Convert comma-separated string to selected objects
    const selected = React.useMemo(() => {
        if (!value) return [];
        const values = value
            .split(",")
            .map(v => v.trim())
            .filter(v => v);
        return innerOptions.filter(option => values.includes(option.value));
    }, [value, innerOptions]);

    const handleUnselect = React.useCallback(
        (framework: { value: string; label: string }) => {
            if (disabled) return;
            const newSelected = selected.filter(s => s.value !== framework.value);
            const newValue = newSelected.map(s => s.value).join(", ");
            onValueChange?.(newValue);
        },
        [selected, onValueChange, disabled]
    );

    const handleSelect = React.useCallback(
        (framework: { value: string; label: string }) => {
            if (disabled) return;
            const newSelected = [...selected, framework];
            const newValue = newSelected.map(s => s.value).join(", ");
            onValueChange?.(newValue);
            setInputValue("");
        },
        [selected, onValueChange, disabled]
    );

    React.useEffect(() => {
        if (optionsUrl) {
            DataService.client
                .url(optionsUrl)
                .get()
                .json<{ display_name: string; value: string }[]>()
                .then(options => {
                    setInnerOptions(
                        options.map(option => ({
                            value: option.value,
                            label: option.display_name
                        }))
                    );
                });
        }
    }, [optionsUrl]);

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled) return;
            const input = inputRef.current;
            if (input) {
                if (e.key === "Delete" || e.key === "Backspace") {
                    if (input.value === "") {
                        const newSelected = [...selected];
                        newSelected.pop();
                        const newValue = newSelected.map(s => s.value).join(", ");
                        onValueChange?.(newValue);
                    }
                }
                // This is not a default behaviour of the <input /> field
                if (e.key === "Escape") {
                    input.blur();
                }
            }
        },
        [selected, onValueChange, disabled]
    );

    const selectables = innerOptions.filter(option => !selected.some(s => s.value === option.value) && option.label.toLowerCase().includes(inputValue.toLowerCase()));
    const dropdownOpen = open && selectables.length > 0 && !disabled;

    // The option list is positioned against the viewport rather than the control, so that
    // scrollable ancestors (the filter popup, CommandList) cannot clip it.
    React.useLayoutEffect(() => {
        if (!dropdownOpen) return;

        const updateDropdownPosition = () => {
            const control = controlRef.current;
            if (!control) return;

            const rect = control.getBoundingClientRect();
            const spaceBelow = window.innerHeight - getViewportBottomInset() - rect.bottom - DROPDOWN_GAP;
            const spaceAbove = rect.top - DROPDOWN_GAP;
            const openUpward = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;

            setDropdownStyle({
                position: "fixed",
                left: rect.left,
                width: rect.width,
                maxHeight: Math.max(0, openUpward ? spaceAbove : spaceBelow),
                ...(openUpward ? { bottom: window.innerHeight - rect.top + DROPDOWN_GAP } : { top: rect.bottom + DROPDOWN_GAP })
            });
        };

        updateDropdownPosition();
        window.addEventListener("resize", updateDropdownPosition);
        window.addEventListener("scroll", updateDropdownPosition, true);

        return () => {
            window.removeEventListener("resize", updateDropdownPosition);
            window.removeEventListener("scroll", updateDropdownPosition, true);
        };
    }, [dropdownOpen]);

    return (
        <Command onKeyDown={handleKeyDown} className="overflow-visible bg-transparent">
            <div
                ref={controlRef}
                className={`group rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
                <div className="flex flex-wrap gap-1">
                    {selected.map(framework => {
                        return (
                            <Badge key={framework.value} variant="secondary">
                                {framework.label}
                                {!disabled && (
                                    <button
                                        className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        onKeyDown={e => {
                                            if (e.key === "Enter") {
                                                handleUnselect(framework);
                                            }
                                        }}
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={() => handleUnselect(framework)}
                                    >
                                        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                    </button>
                                )}
                            </Badge>
                        );
                    })}
                    {/* Avoid having the "Search" Icon */}
                    <CommandPrimitive.Input
                        ref={inputRef}
                        value={inputValue}
                        onValueChange={setInputValue}
                        onBlur={() => setOpen(false)}
                        onFocus={() => !disabled && setOpen(true)}
                        placeholder={selected.length === 0 ? placeholder : ""}
                        className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
                        disabled={disabled}
                    />
                </div>
            </div>
            <CommandList>
                {dropdownOpen ? (
                    <div className="z-50 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in" style={dropdownStyle}>
                        <CommandGroup className="h-full max-h-48 overflow-auto">
                            {selectables.map(framework => {
                                return (
                                    <CommandItem
                                        key={framework.value}
                                        onMouseDown={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onSelect={() => {
                                            handleSelect(framework);
                                        }}
                                        className={"cursor-pointer"}
                                    >
                                        {framework.label}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </div>
                ) : null}
            </CommandList>
        </Command>
    );
}
